import { api } from './api';
import { EventCategory } from '../types/event-category';
import { buildApiUrl } from '../utils/apiUrlConfig';

export interface Category {
  id: string;
  name: string;
  eventCount?: number;
  isDefault?: boolean;
  color?: string;
  icon?: string;
  subcategories?: Category[];
}

class CategoryService {
  private static _instance: CategoryService;
  private _categories: Category[] = [];
  private _isLoaded = false;

  private constructor() {
    // Sera chargé à partir de l'API maintenant
  }

  public static getInstance(): CategoryService {
    if (!CategoryService._instance) {
      CategoryService._instance = new CategoryService();
    }
    return CategoryService._instance;
  }

  /**
   * Récupère toutes les catégories depuis l'API ou le cache
   */
  public async getCategories(forceRefresh = false): Promise<Category[]> {
    if (!this._isLoaded || forceRefresh) {
      try {
        // Charger les catégories depuis l'API
        await this._fetchCategoriesFromApi();
        this._isLoaded = true;
      } catch (error) {
        console.error('Erreur lors du chargement des catégories:', error);
        // En cas d'erreur, utiliser les catégories par défaut
        this._resetToDefaultCategories();
      }
    }
    
    return [...this._categories];
  }

  /**
   * Récupère les catégories depuis l'API
   */
  private async _fetchCategoriesFromApi(): Promise<void> {
    try {
      const { data } = await api.get(buildApiUrl('/event-categories'));
      
      if (Array.isArray(data)) {
        this._categories = data.map((cat: EventCategory) => ({
          id: cat.code,
          name: cat.name,
          color: cat.color,
          icon: cat.icon,
          isDefault: cat.isDefault,
          eventCount: 0
        }));
      } else {
        console.error('Format de réponse API inattendu:', data);
        this._resetToDefaultCategories();
      }
    } catch (error) {
      console.error('Erreur lors de la récupération des catégories depuis l\'API:', error);
      throw error;
    }
  }

  /**
   * Récupère et normalise une catégorie en fonction du texte
   * Cette méthode est cruciale pour la conversion entre les catégories
   * stockées en base et les valeurs historiques dans l'application
   */
  public normalizeCategory(categoryText: string): string {
    const normalized = categoryText.toLowerCase().trim();
    
    // Mappings de normalisation (permet de gérer les variations orthographiques)
    const mappings: Record<string, string> = {
      'conférence': 'conference',
      'conference': 'conference',
      'atelier': 'workshop',
      'workshop': 'workshop',
      'meetup': 'meetup',
      'rencontre': 'meetup',
      'hackathon': 'hackathon',
      'webinaire': 'webinar',
      'webinar': 'webinar',
      'séminaire': 'webinar',
      'seminaire': 'webinar',
      'formation': 'workshop'
    };
    
    return mappings[normalized] || 'other';
  }

  /**
   * Récupère une catégorie par son ID
   */
  public async getCategoryById(id: string): Promise<Category | undefined> {
    await this.getCategories();
    return this._categories.find(cat => cat.id === id);
  }

  /**
   * Ajoute une nouvelle catégorie
   */
  public async addCategory(name: string, options: Partial<Category> = {}): Promise<Category> {
    // Validation
    if (!name.trim()) {
      throw new Error('Le nom de la catégorie ne peut pas être vide');
    }
    
    await this.getCategories();
    
    // Vérifier si la catégorie existe déjà
    if (this._categories.some(cat => cat.name.toLowerCase() === name.toLowerCase())) {
      throw new Error('Cette catégorie existe déjà');
    }
    
    try {
      // Créer la catégorie via l'API
      const slugifiedId = name.toLowerCase().replace(/\s+/g, '_');
      
      const { data } = await api.post('/event-categories', {
        code: slugifiedId,
        name,
        color: options.color || '#ADB5BD',
        icon: options.icon,
        isDefault: options.isDefault || false
      });
      
      // Convertir la réponse API au format Category
      const newCategory: Category = {
        id: data.code,
        name: data.name,
        eventCount: 0,
        color: data.color,
        icon: data.icon,
        isDefault: data.isDefault
      };
      
      // Ajouter au cache local
      this._categories.push(newCategory);
      
      return newCategory;
    } catch (error) {
      console.error('Erreur lors de l\'ajout de la catégorie:', error);
      throw error;
    }
  }

  /**
   * Met à jour une catégorie existante
   */
  public async updateCategory(id: string, categoryData: Partial<Category>): Promise<Category> {
    try {
      // Supprimer les champs non-modifiables
      const { eventsCount, ...updateData } = categoryData as any;
      
      // Appel API pour mettre à jour la catégorie
      await api.put(`/event-categories/${id}`, {
        name: updateData.name,
        color: updateData.color,
        icon: updateData.icon
      });
      
      // Récupérer la catégorie mise à jour pour avoir les données les plus récentes
      const { data } = await api.get(`/event-categories/${id}`);
      return data;
    } catch (error) {
      console.error('Erreur lors de la mise à jour de la catégorie:', error);
      throw error;
    }
  }

  /**
   * Supprime une catégorie
   */
  public async deleteCategory(id: string): Promise<void> {
    await this.getCategories();
    
    const categoryIndex = this._categories.findIndex(cat => cat.id === id);
    if (categoryIndex === -1) {
      throw new Error('Catégorie non trouvée');
    }
    
    // Empêcher la suppression des catégories par défaut
    if (this._categories[categoryIndex].isDefault) {
      throw new Error('Impossible de supprimer une catégorie par défaut');
    }
    
    try {
      // Supprimer via l'API
      await api.delete(`/event-categories/${id}`);
      
      // Mettre à jour le cache local
      this._categories.splice(categoryIndex, 1);
    } catch (error) {
      console.error('Erreur lors de la suppression de la catégorie:', error);
      throw error;
    }
  }

  /**
   * Réinitialise les catégories à leurs valeurs par défaut
   */
  private _resetToDefaultCategories(): void {
    this._categories = [
      { id: 'conference', name: 'Conférence', isDefault: true, color: '#4C6EF5' },
      { id: 'workshop', name: 'Atelier', isDefault: true, color: '#12B886' },
      { id: 'meetup', name: 'Meetup', isDefault: true, color: '#FA5252' },
      { id: 'hackathon', name: 'Hackathon', isDefault: true, color: '#7950F2' },
      { id: 'webinar', name: 'Webinaire', isDefault: true, color: '#FD7E14' },
      { id: 'other', name: 'Autre', isDefault: true, color: '#ADB5BD' }
    ];
  }

  /**
   * Récupère les statistiques des catégories (nombre d'événements par catégorie)
   */
  public async getCategoriesStats(): Promise<Category[]> {
    try {
      // D'abord, nous avons besoin de récupérer les catégories normales
      await this.getCategories();
      
      console.log('Catégories avant stats:', this._categories);
      
      try {
        // Utiliser le bon endpoint avec le préfixe /api
        const response = await fetch('/api/event-categories/stats', {
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json'
          }
        });
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        // Vérifier si la réponse est du JSON
        const contentType = response.headers.get('content-type');
        if (!contentType || !contentType.includes('application/json')) {
          throw new Error(`Expected JSON response but got ${contentType}`);
        }
        
        const data = await response.json();
        console.log('Données de statistiques reçues:', data);
        
        if (Array.isArray(data)) {
          // Mettre à jour les compteurs d'événements
          const categoriesWithStats = this._categories.map(cat => {
            // Trouver la statistique qui correspond au code de cette catégorie
            const categoryStats = data.find((s: any) => s.code === cat.id);
            
            console.log(`Correspondance pour ${cat.id}:`, categoryStats);
            
            return {
              ...cat,
              eventCount: categoryStats ? (categoryStats.count || categoryStats.eventsCount || 0) : 0
            };
          });
          
          console.log('Catégories avec stats:', categoriesWithStats);
          return categoriesWithStats;
        }
      } catch (apiError) {
        console.error('Erreur API spécifique:', apiError);
        // Continuer avec les catégories sans stats en cas d'erreur API
      }
      
      // Si on arrive ici, soit l'API a échoué, soit les données n'étaient pas au bon format
      console.warn('Utilisation des catégories sans stats (erreur ou format incorrect)');
      return this._categories;
    } catch (error) {
      console.error('Erreur lors de la récupération des statistiques des catégories:', error);
      return this._categories;
    }
  }

  /**
   * Récupère les catégories formatées comme options pour les menus déroulants
   * Format requis par le composant EventForm
   */
  public async getCategoriesAsOptions(): Promise<{value: string, label: string}[]> {
    try {
      // Récupérer les catégories
      const categories = await this.getCategories();
      
      // Transformer les catégories en options de formulaire
      return categories.map(category => ({
        value: category.id, // Le code de la catégorie comme valeur
        label: category.name // Le nom de la catégorie comme libellé
      }));
    } catch (error) {
      console.error('Erreur lors de la récupération des options de catégories:', error);
      return [];
    }
  }
}

// Exporter une instance unique du service
export const categoryService = CategoryService.getInstance();
